import type { TFunction } from 'react-i18next';
import { DEFAULT_PATHS } from 'routes/paths';
import { businessAppDetailsUrl } from 'pages/BusinessApps/constants';
import { EMPTY_VALUE } from 'ts-components/Plugins/businessApps/constants';
import {
  type AppNotice,
  type BusinessAppAction,
  type BusinessAppPermissions,
  appNotices,
  countMissingAppSettings,
  countMissingUserSettings,
  getVersionActions,
  isAppDisabled,
  liveOrParkedVersionOf,
  publishedByOf,
} from 'ts-components/Plugins/businessApps/helpers';
import type {
  BusinessApp,
  BusinessAppVersion,
} from 'ts-components/Plugins/businessApps/types';
import type {
  BusinessPluginApp,
  PluginConfig,
} from 'ts-components/Plugins/types';
import type { MarketplacePlugin } from 'pages/Marketplace/types';

export type InstalledAppType = 'business' | 'marketplace';
export type InstalledAttention = 'errors' | 'warnings';

export type InstalledAppRow = {
  id: string;
  apiName: string;
  name: string;
  type: InstalledAppType;
  enabled: boolean;
  version: string;
  updated: string | null;
  publishedBy: string;
  detailsUrl: string | null;
  issues: AppNotice[];
  health: 'error' | 'warning' | null;
  usingPreview: boolean;
  app?: BusinessApp;
  liveVersion?: BusinessAppVersion;
  marketplacePlugin?: MarketplacePlugin;
  actions: BusinessAppAction[];
};

export type InstalledFilters = {
  type: InstalledAppType | null;
  attention: InstalledAttention | null;
};

export type InstalledOrdering =
  | 'name'
  | '-name'
  | 'type'
  | '-type'
  | 'status'
  | '-status'
  | 'updated'
  | '-updated'
  | 'published_by'
  | '-published_by';

const UPGRADE_SUBPATH = '/version';

export const buildInstalledRows = ({
  t,
  businessPluginApps,
  businessApps,
  plugins,
  permissions,
  currentUserId,
  canManageMarketplaceApps = false,
  defaultPluginApps = [],
}: {
  t: TFunction;
  businessPluginApps: BusinessPluginApp[];
  businessApps: BusinessApp[];
  plugins: PluginConfig;
  permissions: BusinessAppPermissions;
  currentUserId?: string;
  canManageMarketplaceApps?: boolean;
  defaultPluginApps?: MarketplacePlugin[];
}): InstalledAppRow[] => {
  const appsByApiName = new Map(
    businessApps.map((businessApp) => [businessApp.api_name, businessApp])
  );

  const installedRows = businessPluginApps.flatMap(
    (businessPluginApp): InstalledAppRow[] => {
      const { plugin_app: pluginApp, config } = businessPluginApp;
      const apiName = pluginApp.api_name;
      const installed = plugins[apiName];
      const isBusiness = businessPluginApp.source === 'business';
      const app = isBusiness ? appsByApiName.get(apiName) : undefined;
      const liveVersion = app && liveOrParkedVersionOf(app);

      if (app && !liveVersion) {
        return [];
      }

      const enabled =
        !businessPluginApp.disabled && !(app && isAppDisabled(app));
      const baseConfig = installed?.base_config ?? pluginApp.base_config;
      const canSeeAppSettings = isBusiness
        ? permissions.canPublishBusinessApps
        : canManageMarketplaceApps;
      const upgradeable = !isBusiness && installed?.upgradeable;
      const issues = enabled
        ? [
            ...appNotices(
              t,
              businessPluginApp.error_count,
              canSeeAppSettings
                ? countMissingAppSettings({ base_config: baseConfig, config })
                : 0,
              countMissingUserSettings({
                base_config: baseConfig,
                employee_config: installed?.employee_config,
              })
            ),
            ...(upgradeable
              ? [
                  {
                    subpath: UPGRADE_SUBPATH,
                    message: t(
                      'Upgrade available from {{version}} to {{newVersion}}.',
                      { version: installed.version, newVersion: upgradeable }
                    ),
                  },
                ]
              : []),
          ]
        : [];
      const hasError = issues.some(
        ({ subpath }) => subpath !== UPGRADE_SUBPATH
      );

      const configurationOverride =
        pluginApp.base_config?.editConfigurationOverride;
      const detailsUrl = isBusiness
        ? permissions.canViewBusinessApps
          ? businessAppDetailsUrl(apiName)
          : null
        : typeof configurationOverride === 'string'
          ? configurationOverride
          : pluginApp.base_config?.disableDetailView
            ? null
            : `${DEFAULT_PATHS.MARKETPLACE}/${apiName}/details`;

      return [
        {
          id: apiName,
          apiName,
          name: app?.name || installed?.name || pluginApp.name || apiName,
          type: isBusiness ? 'business' : 'marketplace',
          enabled,
          version:
            (isBusiness ? liveVersion?.version : installed?.version) ||
            EMPTY_VALUE,
          updated: businessPluginApp.updated ?? pluginApp.updated ?? null,
          publishedBy:
            (isBusiness
              ? app && publishedByOf(app, liveVersion)?.full_name
              : pluginApp.managed_by_business?.name) || EMPTY_VALUE,
          detailsUrl,
          issues,
          health: hasError ? 'error' : issues.length ? 'warning' : null,
          usingPreview: app?.current_version?.status === 'preview',
          app,
          liveVersion,
          marketplacePlugin: isBusiness ? undefined : pluginApp,
          actions: isBusiness
            ? app && liveVersion
              ? getVersionActions(app, liveVersion, permissions, currentUserId)
              : []
            : [
                ...(detailsUrl ? (['view'] as const) : []),
                ...(canManageMarketplaceApps &&
                !pluginApp.base_config?.forceEnable
                  ? ([enabled ? 'disable' : 'enable'] as const)
                  : []),
              ],
        },
      ];
    }
  );

  const installedApiNames = new Set(installedRows.map((row) => row.apiName));
  const builtInRows = defaultPluginApps
    .filter((pluginApp) => !installedApiNames.has(pluginApp.api_name))
    .map((pluginApp): InstalledAppRow => {
      const detailsUrl =
        typeof pluginApp.base_config?.editConfigurationOverride === 'string'
          ? pluginApp.base_config.editConfigurationOverride
          : null;
      return {
        id: pluginApp.api_name,
        apiName: pluginApp.api_name,
        name: pluginApp.name || pluginApp.api_name,
        type: 'marketplace',
        enabled: true,
        version: plugins[pluginApp.api_name]?.version || EMPTY_VALUE,
        updated: pluginApp.updated ?? null,
        publishedBy: pluginApp.managed_by_business?.name || EMPTY_VALUE,
        detailsUrl,
        issues: [],
        health: null,
        usingPreview: false,
        marketplacePlugin: pluginApp,
        actions: detailsUrl ? ['view'] : [],
      };
    });

  return [...installedRows, ...builtInRows];
};

export const filterInstalledRows = (
  rows: InstalledAppRow[],
  search: string,
  typeLabels: Record<InstalledAppType, string>,
  { type, attention }: InstalledFilters
) => {
  const query = search.trim().toLowerCase();
  return rows.filter(
    (row) =>
      (!query ||
        row.name.toLowerCase().includes(query) ||
        typeLabels[row.type].toLowerCase().includes(query)) &&
      (!type || row.type === type) &&
      (attention !== 'errors' || row.health === 'error') &&
      (attention !== 'warnings' ||
        row.issues.some(({ subpath }) => subpath === UPGRADE_SUBPATH))
  );
};

const toTime = (date: string | null) => (date ? Date.parse(date) || 0 : 0);

const compareText = (a: string, b: string) =>
  a.toLowerCase().localeCompare(b.toLowerCase());

const SORT_COMPARATORS: Record<
  string,
  (a: InstalledAppRow, b: InstalledAppRow) => number
> = {
  name: (a, b) => compareText(a.name, b.name),
  type: (a, b) => compareText(a.type, b.type),
  status: (a, b) => Number(b.enabled) - Number(a.enabled),
  updated: (a, b) => toTime(a.updated) - toTime(b.updated),
  published_by: (a, b) => compareText(a.publishedBy, b.publishedBy),
};

export const sortInstalledRows = (
  rows: InstalledAppRow[],
  ordering: InstalledOrdering
) => {
  const descending = ordering.startsWith('-');
  const compare = SORT_COMPARATORS[ordering.replace('-', '')];
  return [...rows].sort((a, b) => compare(a, b) * (descending ? -1 : 1));
};

export const hasBusinessRows = (rows: InstalledAppRow[]) =>
  rows.some(({ type }) => type === 'business');

export const issueUrl = (row: InstalledAppRow, subpath: string) =>
  row.type === 'business'
    ? businessAppDetailsUrl(row.apiName, null, subpath)
    : `${DEFAULT_PATHS.MARKETPLACE}/${row.apiName}${subpath}`;
